/**
 * PayrollHeader.tsx - Save button, change log, monthly data info panel
 *
 * Phase 2: payroll-management.tsx component decomposition
 * FR-9: Extracted JSX section
 */

import React from 'react';
import { usePayrollRender } from './PayrollRenderContext.tsx';

export const PayrollHeader: React.FC = () => {
  const {
    CanEdit,
    budgetData,
    changeLog,
    contextLastSaveTime,
    employees,
    globalEmployees,
    internalSaveAllData,
    saveLimitedPayrollData,
    payrollEditMode,
    isDataChanged,
    isSaving,
    lastSaveTime,
    selectedMonth,
  } = usePayrollRender();

  return (
    <>
      <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm mb-3">
        {/* 저장 버튼과 변경사항 정보 */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            {/* 🔒 저장 버튼은 권한에 따라 두 갈래다.
                - full(관리자): 전체 저장 — 화면의 모든 값을 기록
                - limited(실장): 연장근로시간·개별수당과 그 파생값만 기록 (다른 값은 DB 최신값 보존)
                CanEdit feature="payroll"는 full 권한만 통과하므로 제한 저장은 별도 분기로 그린다. */}
            <CanEdit feature="payroll">
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 sm:py-2 sm:px-4 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60 text-sm sm:text-base transition-colors"
                onClick={internalSaveAllData}
                disabled={isSaving || !isDataChanged}
              >
                {isSaving ? '저장 중...' : (isDataChanged ? '변경사항 저장 및 업데이트*' : '저장 완료')}
              </button>
            </CanEdit>
            {payrollEditMode === 'limited' && (
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 sm:py-2 sm:px-4 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60 text-sm sm:text-base transition-colors"
                onClick={saveLimitedPayrollData}
                disabled={isSaving || !isDataChanged}
                title="연장근로시간·개별수당만 저장합니다. 월급 등 다른 값은 변경되지 않습니다."
              >
                {isSaving ? '저장 중...' : (isDataChanged ? '연장근로시간·개별수당 저장*' : '저장 완료')}
              </button>
            )}
            {isDataChanged && !isSaving && (
              <div className="flex flex-col">
                <span className="text-yellow-600 text-sm font-medium">⚠️ 변경사항이 있습니다.</span>
                {changeLog.length > 0 && (
                  <div className="text-xs text-yellow-900 mt-2 p-2 bg-yellow-50 rounded border border-yellow-200 max-h-24 overflow-y-auto">
                    <div className="font-medium mb-1">최근 변경사항:</div>
                    {changeLog.slice(-3).map((change, index) => (
                      <div key={index} className="flex items-start">
                        <span className="text-yellow-700 mr-1">•</span>
                        <span>{change}</span>
                      </div>
                    ))}
                    {changeLog.length > 3 && (
                      <div className="text-yellow-800 text-xs mt-1">외 {changeLog.length - 3}개 항목...</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* 저장 시간 표시 */}
          <div className="text-sm text-gray-600 sm:text-right">
            {contextLastSaveTime && (
              <span className="flex items-center sm:justify-end">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                마지막 저장: {contextLastSaveTime}
              </span>
            )}
            {lastSaveTime && !contextLastSaveTime && (
               <span className="text-xs text-gray-500">(내부: {lastSaveTime})</span>
            )}
          </div>
        </div>
        
        {/* 월별 데이터 정보 */}
        <div className="border-t pt-3">
          <div className="flex flex-col lg:flex-row lg:justify-between gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <div>
                <span className="text-sm font-medium text-blue-700">
                  {selectedMonth.replace('-', '년 ')}월 급여 데이터
                </span>
                {budgetData.monthlyData && budgetData.monthlyData[selectedMonth]?.employees ? 
                  <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">✅ 월별 저장 데이터</span> : 
                  (globalEmployees.length > 0 ? 
                    <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">🌍 Firebase 전역 데이터</span> :
                    <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">⚠️ 새 데이터</span>
                  )
                }
              </div>
              <div className="text-xs text-gray-600">
                {budgetData.monthlyData && budgetData.monthlyData[selectedMonth]?.employees ?
                  `직원 ${budgetData.monthlyData[selectedMonth].employees.length}명 (월별 저장)` : 
                  (globalEmployees.length > 0 ? 
                    `직원 ${employees.length}명 (Firebase 기반)` :
                    `직원 ${employees.length}명 (신규)`
                  )
                }
                {budgetData.monthlyData?.[selectedMonth]?.dataSource && 
                  <span className="ml-2 text-gray-500">
                    • {budgetData.monthlyData[selectedMonth].dataSource}
                  </span>
                }
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 text-xs">
              <div>
                <span className="font-medium text-gray-700">월별 비교: </span>
                {(() => {
                  if (!budgetData.monthlyData) return '데이터 없음';
                  
                  const monthsWithData = Object.keys(budgetData.monthlyData || {})
                    .filter(month => budgetData.monthlyData?.[month].employees)
                    .sort((a, b) => b.localeCompare(a));
                  
                  if (monthsWithData.length <= 1) return '비교 가능한 다른 월 데이터 없음';
                  
                  return (
                    <span className="text-green-600 font-medium">
                      {monthsWithData.length}개 월 비교 가능
                    </span>
                  );
                })()}
              </div>
              
              <div>
                {(() => {
                  const currentYearMonth = new Date().toISOString().slice(0, 7);
                  return selectedMonth === currentYearMonth ?
                    <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">현재 월</span> :
                    <span className="text-gray-600">현재 월 아님 (글로벌 데이터에 반영되지 않음)</span>;
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
